import React from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import NumberFormat from 'react-number-format'
import media from '../../utils/style'
import IconHeart from '../../vectors/IconHeart'
import { lighten } from 'polished'
import { withFavouritesContext } from '../../context/favourites'

const Card = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  text-decoration: none;

  &:hover,
  &:focus,
  &:active {
    .OfferCardImage {
      transition: 0.2s ease-in;
      transform: scale(1.1);
      opacity: 0.9;
    }
  }
`

const CardImageWrapper = styled.div`
  position: relative;
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  aspect-ratio: 16 / 9;
  border: 1px solid #c6c6c6;
`

const CardImage = styled.img`
  width: 100%;
  margin: 0;
  transition: 0.1s ease-out;
`

const CardLabel = styled.div`
  position: absolute;
  top: 16px;
  left: 16px;
  font-size: 0.75rem; // 12px
  line-height: 1;
  font-weight: 700;
  background-color: white;
  color: ${(props) => props.theme.colors.black};
  padding: 6px 8px;
  border-radius: 8px;
  text-transform: uppercase;

  ${media.MediumScreenUp`
    font-size: 0.8125rem; // 13px
  `}
`

const CardFavoriteButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  border: 0;
  background: transparent;
  cursor: pointer;

  svg {
    transition: transform 0.2s ${(props) => props.theme.ease.ease_out_back};
    transform-origin: 50%;
  }

  path {
    transition: fill 0.5s ${(props) => props.theme.ease.ease_out_expo};
    transform-origin: 50%;
  }

  path:first-child {
    transform: scale(1.1) translateZ(0);
    fill: ${(props) => props.theme.colors.grey_op65};
  }

  path:last-child {
    fill: ${(props) => props.theme.colors.white};
  }

  &:hover path:last-child {
    fill: ${(props) => props.theme.colors.blue};
  }

  &.favorite svg {
    transform: scale(1.1) translateZ(0);
    path {
      fill: ${(props) => props.theme.colors.blue};
    }
  }
  &.favorite:hover svg {
    transform: scale(1.15) translateZ(0);
    path {
      fill: ${(props) => lighten(0.1, props.theme.colors.blue)};
    }
  }
`

const CardContent = styled.a`
  display: flex;
  flex-direction: column;
  padding: 20px;
  border-radius: 0 0 16px 16px;
  border: 1px solid #c6c6c6;
  border-top: 0;
  flex-grow: 1;
  text-decoration: none;

  ${media.MediumScreenUp`
    padding: 24px;
  `}
`

const CardTitle = styled.h3`
  font-size: 0.9375rem; // 15px
  font-weight: 700;
  padding-bottom: 4px;
  color: ${(props) => props.theme.colors.black};
  margin: 0;

  ${media.MediumScreenUp`
    font-size: 1.125rem; // 18px
  `}
`

const CardSubHeading = styled.p`
  font-size: 0.75rem; // 12px
  line-height: 1rem;
  color: ${(props) => props.theme.colors.black};
  margin: 0;

  ${media.MediumScreenUp`
    font-size: 0.8125rem; // 13px
    line-height: 1.1rem;
  `}
`

const CardTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding-top: 16px;
`

const CardTag = styled.div`
  font-size: 0.8125rem; // 13px
  line-height: 1;
  font-weight: 700;
  color: ${(props) => props.theme.colors.black};
  background-color: ${(props) =>
    props.highlighted
      ? props.theme.colors.yellow
      : props.theme.colors.grey_light_op30};
  padding: 4px 8px 5px;
  border-radius: 6px;
`

const CardPriceWrapper = styled.div`
  margin-top: auto;
  padding-top: 24px;
`

const CardSeparator = styled.div`
  width: 100%;
  border-top: 1px solid #c6c6c6;
  padding-bottom: 12px;
`

const CardPrice = styled.div`
  color: ${(props) => props.theme.colors.blue};
  font-size: 1.125rem; // 18px
  line-height: 1.6rem;
  font-weight: 700;

  ${media.MediumScreenUp`
    font-size: 1.375rem; // 22px
    line-height: 2rem;
  `}
`

const CardPriceInstallment = styled.div`
  font-size: 0.8125rem; // 13px
  line-height: 1rem;
  color: ${(props) => props.theme.colors.black};

  ${media.MediumScreenUp`
    font-size: 0.875rem; // 14px
    line-height: 1.2rem;
  `}
`

const OfferCard = (props) => {
  const {
    image,
    heading,
    subheading,
    label,
    details,
    price,
    currency,
    registrationNumber,
    href
  } = props

  const toggleFavourite = (e) => {
    if (e) e.preventDefault()
    props.context.toggle(props.id)
  }

  return (
    <Card>
      <CardImageWrapper>
        <a href={href} tabIndex="-1" aria-hidden="true">
          <CardImage className="OfferCardImage" src={image} alt={heading} />
        </a>
        <CardLabel>{label}</CardLabel>
        <CardFavoriteButton
          onClick={toggleFavourite}
          className={
            props.context && props.context.isFavourited(props.id)
              ? 'favorite'
              : null
          }
        >
          <IconHeart />
        </CardFavoriteButton>
      </CardImageWrapper>
      <CardContent href={href}>
        <CardTitle>{heading}</CardTitle>
        {subheading && <CardSubHeading>{subheading}</CardSubHeading>}
        {((details && details.length > 0) || !!registrationNumber) && (
          <CardTags>
            {details.map((detail) => (
              <CardTag key={detail}>{detail}</CardTag>
            ))}
            {registrationNumber && (
              <CardTag highlighted>{registrationNumber}</CardTag>
            )}
          </CardTags>
        )}
        {price && (
          <CardPriceWrapper>
            <CardSeparator />
            <CardPrice>
              <NumberFormat
                value={price.total}
                displayType={'text'}
                thousandSeparator
                prefix={currency.symbol}
              />
            </CardPrice>
            <CardPriceInstallment>
              <NumberFormat
                value={price.installment.payment}
                displayType={'text'}
                thousandSeparator
                prefix={currency.symbol}
                decimalScale={2}
                fixedDecimalScale
              />
              /{price.installment.plan} ({price.installment.type.toUpperCase()})
            </CardPriceInstallment>
          </CardPriceWrapper>
        )}
      </CardContent>
    </Card>
  )
}

export const OfferCardPropTypes = {
  id: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  heading: PropTypes.string.isRequired,
  subheading: PropTypes.string,
  label: PropTypes.string,
  details: PropTypes.arrayOf(PropTypes.string),
  registrationNumber: PropTypes.string,
  price: PropTypes.shape({
    total: PropTypes.number,
    installment: PropTypes.shape({
      payment: PropTypes.number,
      plan: PropTypes.string,
      code: PropTypes.string,
      type: PropTypes.string
    })
  }),
  currency: PropTypes.shape({
    code: PropTypes.string,
    symbol: PropTypes.string
  })
}

OfferCard.propTypes = OfferCardPropTypes

export default withFavouritesContext(OfferCard)
